import { GameObject, Rectangle, Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class Ball implements GameObject {
  public id: string;
  public active: boolean = true;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  
  // Ball properties
  private ballSize: number;
  private animationTimer: number = 0;
  private trailPositions: Vector2[] = [];
  private readonly MAX_TRAIL_LENGTH = 8;

  constructor(
    id: string,
    x: number,
    y: number,
    size: number,
    renderManager: RenderManager,
    audioManager: AudioManager
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: 0, y: 0 };
    this.ballSize = size;
    this.size = { x: size, y: size };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
  }

  public update(deltaTime: number): void {
    // Update animation timer
    this.animationTimer += deltaTime;
    
    // Store current position for trail effect
    this.trailPositions.unshift({ ...this.position });
    if (this.trailPositions.length > this.MAX_TRAIL_LENGTH) {
      this.trailPositions.pop();
    }
    
    // Update position based on velocity
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
  }

  public setPosition(x: number, y: number): void {
    this.position.x = x;
    this.position.y = y;
    // Clear trail when teleporting
    this.trailPositions = [];
  }

  public setVelocity(x: number, y: number): void {
    this.velocity.x = x;
    this.velocity.y = y;
  }

  public getVelocity(): Vector2 {
    return { ...this.velocity };
  }

  public getPosition(): Vector2 {
    return { ...this.position };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.ballSize,
      height: this.ballSize
    };
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;
    
    const centerX = this.position.x + this.ballSize / 2;
    const centerY = this.position.y + this.ballSize / 2;
    const radius = this.ballSize / 2;
    
    // Draw trail effect
    this.trailPositions.forEach((trailPos, index) => {
      if (index === 0) return; // Skip current position
      
      const alpha = 1 - (index / this.MAX_TRAIL_LENGTH);
      const trailRadius = radius * (1 - index * 0.1);
      const trailCenterX = trailPos.x + this.ballSize / 2;
      const trailCenterY = trailPos.y + this.ballSize / 2;
      
      if (trailRadius > 1) {
        this.renderManager.drawCircle(
          trailCenterX,
          trailCenterY,
          trailRadius,
          true,
          `rgba(255, 255, 255, ${alpha * 0.3})`,
          1
        );
      }
    });
    
    // Main ball body
    this.renderManager.drawCircle(centerX, centerY, radius, true, '#FFFFFF', 1);
    
    // Inner glow effect
    const glowRadius = radius * 0.6;
    this.renderManager.drawCircle(centerX, centerY, glowRadius, true, '#FFFF00', 1);
    
    // Rotating highlight
    const highlightAngle = this.animationTimer * 4;
    const highlightX = centerX + Math.cos(highlightAngle) * (radius * 0.3);
    const highlightY = centerY + Math.sin(highlightAngle) * (radius * 0.3);
    this.renderManager.drawCircle(highlightX, highlightY, 2, true, '#FFFFFF', 1);
    
    // Pulsing outer glow when moving fast
    const speed = Math.sqrt(this.velocity.x * this.velocity.x + this.velocity.y * this.velocity.y);
    if (speed > 200) {
      const glowIntensity = 0.3 + Math.sin(this.animationTimer * 8) * 0.2;
      const glowColor = `rgba(255, 255, 0, ${glowIntensity})`;
      this.renderManager.drawCircle(centerX, centerY, radius + 4, false, glowColor, 2);
    }
  }
}